import {
    LogoutOutlined,
    SettingOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { outLogin } from '@/services/ant-design-pro/api';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
    menu?: boolean;
    children?: React.ReactNode;
};

export const AvatarName = () => {
    const { initialState } = useModel('@@initialState');
    const { currentUser } = initialState || {};
    return <span className="anticon">{currentUser?.username}</span>;
};

const useStyles = createStyles(({ token }) => {
    return {
        action: {
            display: 'flex',
            height: '48px',
            marginLeft: 'auto',
            overflow: 'hidden',
            alignItems: 'center',
            padding: '0 8px',
            cursor: 'pointer',
            borderRadius: token.borderRadius,
            '&:hover': {
                backgroundColor: token.colorBgTextHover,
            },
        },
    };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
    menu,
    children,
}) => {
    /**
     * 退出登录，并且将当前的 url 保存
     */
    const loginOut = async () => {
        // 2. 核心清理逻辑：移除 Token 及过期时间
        localStorage.removeItem('spx_token');
        localStorage.removeItem('spx_token_exp_time');

        // 3. 获取当前路径，用于登录后的“重定向回原处”
        const { pathname, search } = window.location;

        // 如果当前已经在登录页，则不需要再次跳转，避免循环
        if (pathname === '/user/login') return;

        // 4. 构建重定向参数
        // 例如当前在 /biz/orders?id=1，退出后变为 /user/login?redirect=%2Fbiz%2Forders%3Fid%3D1
        const searchParams = new URLSearchParams({
            redirect: pathname + search,
        });

        // 5. 跳转到登录页
        // 使用 replace 而不是 push，防止用户点击浏览器“后退”回到已退出的页面
        history.replace({
            pathname: '/user/login',
            search: searchParams.toString(),
        });
    };
    const { styles } = useStyles();

    const { initialState, setInitialState } = useModel('@@initialState');

    const onMenuClick: MenuProps['onClick'] = (event) => {
        const { key } = event;
        if (key === 'logout') {
            flushSync(() => {
                setInitialState((s) => ({ ...s, currentUser: undefined }));
            });
            loginOut();
            return;
        }
        history.push(`/account/${key}`);
    };

    const loading = (
        <span className={styles.action}>
            <Spin
                size="small"
                style={{
                    marginLeft: 8,
                    marginRight: 8,
                }}
            />
        </span>
    );

    if (!initialState) {
        return loading;
    }

    const { currentUser } = initialState;

    if (!currentUser || !currentUser.username) {
        return loading;
    }

    const menuItems = [
        ...(menu
            ? [
                {
                    key: 'center',
                    icon: <UserOutlined />,
                    label: '个人中心',
                },
                {
                    key: 'settings',
                    icon: <SettingOutlined />,
                    label: '个人设置',
                },
                {
                    type: 'divider' as const,
                },
            ]
            : []),
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: '退出登录',
        },
    ];

    return (
        <HeaderDropdown
            menu={{
                selectedKeys: [],
                onClick: onMenuClick,
                items: menuItems,
            }}
        >
            {children}
        </HeaderDropdown>
    );
};
